import { useEffect } from 'react';
import { ImageField, ImageInput, useTranslate } from 'react-admin';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { useFormContext } from 'react-hook-form';
import { useNotify } from 'react-admin';

const validTypes = ['image/png', 'image/jpg', 'image/jpeg'];

interface Props {
    source: string;
    label: string;
    readOnly?: boolean;
}

const ImageInputWithLabelOnTop = ({ source, label, readOnly = false }: Props) => {
    const { watch, resetField } = useFormContext();
    const translate = useTranslate();
    const notify = useNotify();

    const image = watch(source);

    useEffect(() => {
        if (image && 'rawFile' in image && 'type' in image.rawFile && !validTypes.includes(image.rawFile.type)) {
            resetField(source);
            notify('messages.only_jpg_or_png_files', {
                type: 'warning',
            });
        }
    }, [image]);

    return image ? (
        <Box
            sx={{
                display: 'grid',
                gap: 1,
                alignItems: 'center',
                '& .previews img': {
                    width: '100%',
                    height: 'auto',
                    m: 0,
                    mt: 0.5,
                    borderRadius: 1,
                },
            }}
        >
            {!readOnly && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr',
                        gap: 1,
                    }}
                >
                    <AddPhotoAlternateOutlinedIcon />

                    <Typography>{label}</Typography>
                </Box>
            )}

            <ImageInput
                disabled={readOnly}
                source={source}
                label={false}
                maxSize={30000000}
                placeholder={translate('forms.image_placeholder')}
                helperText={false}
                sx={{
                    '& .RaFileInput-dropZone': {
                        minHeight: image ? 50 : 150,
                        display: readOnly ? 'none' : 'grid',
                        alignItems: 'center',
                        border: (theme: any) => `1px solid ${theme.palette.action.disabled}`,
                        color: (theme: any) => theme.palette.action.disabled,
                    },
                    '& button': {
                        ...(readOnly && {
                            display: 'none',
                        }),
                    },
                    '& img': {
                        maxHeight: 200,
                    },
                    '& .previews': {
                        display: 'flex',
                        justifyContent: 'center',
                    },
                }}
                data-cy='image-input'
            >
                <ImageField source='src' title='title' />
            </ImageInput>
        </Box>
    ) : null;
};

export default ImageInputWithLabelOnTop;
